import React from "react";
import { cn } from "../../lib/utils";
import VoicePlayer from "./VoicePlayer";
import MessageActions from "./MessageActions";
import AppRatingInline from "./AppRatingInline";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp"];

export default function MessageBubble({
  message,
  isOutgoing,
  mediaUrl,
  showSenderName = false,
  onDeleteForMe,
  onDeleteForAll,
  canManageMessages = false,
  appRatingToken,
  existingAppRating,
  onAppRated,
  className,
}) {
  const out = isOutgoing;
  const showAppRating =
    !out &&
    appRatingToken &&
    (message.message_type === "app_rating_prompt" ||
      message.interactive?.type === "app_rating");

  const bubbleCn = cn(
    "max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm",
    out
      ? "bg-primary text-white rounded-br-md"
      : "bg-card text-foreground border border-border rounded-bl-md",
    className,
  );

  return (
    <div className={cn("flex", out ? "justify-end" : "justify-start")}>
      <div className={bubbleCn}>
        <div
          className={cn(
            "flex items-start gap-2",
            out ? "flex-row-reverse" : "flex-row",
          )}
        >
          <div className="flex-1 min-w-0">
            {message.masked ? (
              <div className="text-red-600 font-semibold text-xs">
                Message deleted
              </div>
            ) : (
              <>
                {showSenderName &&
                  (message.sender_role === "staff" ||
                    message.sender_role === "admin") && (
                    <div
                      className={cn(
                        "text-[11px] font-semibold mb-1",
                        out ? "text-white/80" : "text-muted-foreground",
                      )}
                    >
                      {message.sender_name}
                      {/* {message.sender_name || (message.sender_role === 'admin' ? 'Admin' : 'Staff')} */}
                    </div>
                  )}
                {message.text ? (
                  <div className="break-words">{message.text}</div>
                ) : null}
                {Array.isArray(message.media) && message.media.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {message.media.map((media) => {
                      const url = mediaUrl(media.id);
                      if (media.kind === "voice" || media.kind === "audio") {
                        return (
                          <VoicePlayer
                            key={media.id}
                            src={url}
                            isOutgoing={out}
                          />
                        );
                      }
                      if (
                        IMAGE_EXTS.includes((media.ext || "").toLowerCase())
                      ) {
                        return (
                          <a
                            key={media.id}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-lg overflow-hidden border border-black/10"
                          >
                            <img
                              src={url}
                              alt={media.original_name || "attachment"}
                              className="max-h-56 w-auto object-contain"
                            />
                          </a>
                        );
                      }
                      return (
                        <a
                          key={media.id}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className={cn(
                            "block text-xs underline",
                            out ? "text-white/90" : "text-muted-foreground",
                          )}
                        >
                          {media.original_name || "Attachment"}
                        </a>
                      );
                    })}
                  </div>
                ) : null}
                {message.deleted_for_all && message.deleted_mode === "soft" && (
                  <div
                    data-testid="soft-deleted-notice"
                    className="text-[10px] italic mt-1 text-red-500"
                  >
                    Deleted by {message.deleted_by_name || "Staff"}
                  </div>
                )}
                {showAppRating && (
                  <AppRatingInline
                    eventId={message.interactive?.event_id}
                    token={appRatingToken}
                    disabled={false}
                    existingAccountRating={existingAppRating}
                    onRated={onAppRated}
                    variant="light"
                  />
                )}
              </>
            )}
          </div>
          {canManageMessages && (
            <div className="shrink-0">
              <MessageActions
                disabled={!onDeleteForMe && !onDeleteForAll}
                onDeleteForMe={onDeleteForMe}
                onDeleteForAll={onDeleteForAll}
              />
            </div>
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-1 mt-1.5",
            out ? "justify-end" : "justify-start",
          )}
        >
          {message.created_at && (
            <span
              data-testid="message-timestamp"
              className={cn(
                "text-[10px]",
                out ? "text-white/60" : "text-muted-foreground",
              )}
            >
              {new Date(message.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          )}
          {out && (
            <span
              data-testid="message-status-icon"
              className="text-white/70 text-[10px] ml-0.5"
              title={
                message.status === "read"
                  ? "Read"
                  : message.status === "delivered"
                    ? "Delivered"
                    : message.status === "sent"
                      ? "Sent"
                      : "Pending"
              }
            >
              {message.status === "read" ? (
                <DoubleTickIcon className="w-3.5 h-3.5 text-blue-200" />
              ) : message.status === "delivered" ? (
                <DoubleTickIcon className="w-3.5 h-3.5" />
              ) : message.status === "sent" ? (
                <SingleTickIcon className="w-3.5 h-3.5" />
              ) : (
                <ClockIcon className="w-3.5 h-3.5" />
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ClockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
    >
      <path d="M8 2a6 6 0 1 1 0 12A6 6 0 0 1 8 2zm0 1a5 5 0 1 0 0 10A5 5 0 0 0 8 3zm.5 2v3.793l2.354 2.353.707-.708L9 8.293V5h-1z" />
    </svg>
  );
}

function SingleTickIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
    >
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
    </svg>
  );
}

function DoubleTickIcon({ className }) {
  return (
    <span className={className} style={{ display: "inline-flex", gap: "1px" }}>
      <SingleTickIcon className={className} />
      <SingleTickIcon className={className} />
    </span>
  );
}
